import { createActionHelpers } from 'vuex-loading';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const mapEntryToServerObject = e => {
    return {
        product: {
            product_id: e.id,
            hidden: e.restrict_visibility_override ? 0 : e.entry_hidden,
            unit_price: e.restrict_price_override ? null : e.entry_price,
            cost: e.entry_cost,
            variants: e.variants
                ? e.variants.map(function(v) {
                      return {
                          variant_id: v.id,
                          hidden: v.restrict_visibility_override
                              ? null
                              : v.entry_hidden,
                          cost: v.entry_cost,
                          price: v.restrict_price_override
                              ? null
                              : v.entry_price,
                      };
                  })
                : [],
        },
    };
};

const mapOverrideToServerParams = pricebook => {
    return {
        name: pricebook.name,
        description: pricebook.description,
        start_at: pricebook.start_at.format('Y-MM-DDTHH:mm:ssZZ'),
        outlet: pricebook.outlets.id,
    };
};

const mapPricebookToServerObject = pricebook => {
    const data = _.pick(pricebook, ['name', 'description']);
    data.start_at = pricebook.start_at.format('Y-MM-DDTHH:mm:ssZZ');

    return data;
};

const pricebookToOverride = {
    name: null,
    description: null,
    outlets: [],
    originalOutlets: [],
    startAt: null,
};

const state = {
    pricebook: {
        base_pricebook: {
            name: null,
        },
    },
    searchMeta: null,
    filters: [],
    pricebookToOverride,
    pricebookId: null,
    productsPoolCount: null,
    averageCost: null,
};

const getters = {
    pricebook: state => state.pricebook,
    pricebookId: state => state.pricebookId,
    productsPoolCount: state => state.productsPoolCount,
    pricebookToOverride: state => state.pricebookToOverride,
    averageCost: state => state.averageCost,
};

const mutations = {
    setPricebookId(state, pricebookId) {
        state.pricebookId = pricebookId;
    },

    setProductsPoolCount(state, count) {
        state.productsPoolCount = count;
    },

    setPricebook(state, pricebook) {
        state.pricebook = pricebook;
        state.pricebookToOverride = pricebook;
        state.original = _.cloneDeep(pricebook);
    },

    setSearchMeta(state, meta) {
        state.searchMeta = meta;
    },

    async setFilters(state, filters) {
        state.filters = filters;
    },

    setPricebookToOverride(state, pricebook) {
        const override = _.cloneDeep(pricebook);
        override.originalOutlets = _.cloneDeep(pricebook.outlets);
        state.pricebookToOverride = override;
    },

    setOverridePricebookAttr(state, { key, value }) {
        state.pricebook[key] = value;
    },

    setAverageCost(state, data) {
        state.averageCost = data;
    },
};

const actions = {
    getSearchOverridePricebook({ commit, dispatch }) {
        startLoading(dispatch, 'get-override-pricebook-meta');

        return axios
            .get(
                Laroute.route(
                    'franchise.inventory.override_pricebook.search.meta',
                ),
            )
            .then(res => {
                commit('setSearchMeta', res.data);
                endLoading(dispatch, 'get-override-pricebook-meta');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-override-pricebook-meta');
                throw err;
            });
    },

    getSearchOverrideMeta({ commit, dispatch }) {
        startLoading(dispatch, 'get-override-pricebook-meta');

        return axios
            .get(
                Laroute.route(
                    'franchise.inventory.override_pricebook.search.meta.override',
                ),
            )
            .then(res => {
                commit('setSearchMeta', res.data);
                endLoading(dispatch, 'get-override-pricebook-meta');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-override-pricebook-meta');
                throw err;
            });
    },

    setPricebookId({ commit }, pricebookId) {
        commit('setPricebookId', pricebookId);
    },

    async createPricebookOverride({ state, dispatch, commit }) {
        const url = Laroute.route(
            'franchise.inventory.override_pricebook.store',
        );

        startLoading(dispatch, 'save-override');

        try {
            const { data } = await axios.post(
                url,
                mapOverrideToServerParams(state.pricebookToOverride),
            );
            endLoading(dispatch, 'save-override');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-override');
            throw e;
        }
    },

    async updateOverridePricebook({ state, dispatch, commit }) {
        const id = state.pricebookToOverride.id;
        const url = Laroute.route(
            'franchise.inventory.override_pricebook.update',
            {
                id,
            },
        );

        startLoading(dispatch, 'save-override-pricebook');
        startLoading(dispatch, 'save-override');

        try {
            const { data } = await axios.patch(
                url,
                mapPricebookToServerObject(state.pricebookToOverride),
            );

            endLoading(dispatch, 'save-override-pricebook');
            endLoading(dispatch, 'save-override');
            commit('setPricebook', data);

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-override-pricebook');
            endLoading(dispatch, 'save-override');
            throw e;
        }
    },

    async getOverridePricebook({ commit, dispatch }, id) {
        startLoading(dispatch, 'fetch-override-pricebook');
        const url = Laroute.route(
            'franchise.inventory.override_pricebook.show',
            { id },
        );

        const { data } = await axios.get(url);

        endLoading(dispatch, 'fetch-override-pricebook');
        commit('setPricebook', data);

        return data;
    },

    async saveEntry({ state, dispatch }, params) {
        const id = state.pricebookId;
        const url = Laroute.route(
            'franchise.inventory.override_pricebook.entries.save',
            {
                id,
            },
        );
        startLoading(dispatch, 'save-override-pricebook-entry');

        try {
            const { data } = await axios.post(
                url,
                mapEntryToServerObject(params),
            );
            endLoading(dispatch, 'save-override-pricebook-entry');

            return { data };
        } catch (e) {
            endLoading(dispatch, 'save-override-pricebook-entry');
            throw e;
        }
    },

    async getOverridePricebookProductsCount(
        { dispatch, commit },
        { pricebookId, filters },
    ) {
        startLoading(dispatch, 'fetch-override-pricebook-products-count');
        const url = Laroute.route(
            'franchise.inventory.override_pricebook.products_count',
            { id: pricebookId },
        );
        const { data } = await axios.get(url, { params: filters });
        endLoading(dispatch, 'fetch-override-pricebook-products-count');

        commit('setProductsPoolCount', data.count);

        return data.count;
    },

    async addBatchEntries({ dispatch }, { pricebookId, filters, params }) {
        const url = Laroute.route(
            'franchise.inventory.override_pricebook.batch_add',
            {
                id: pricebookId,
            },
        );

        startLoading(dispatch, 'add-batch-override-entries');
        try {
            const { data } = await axios.post(url, {
                filters: filters,
                params: params,
            });

            endLoading(dispatch, 'add-batch-override-entries');

            return data;
        } catch (e) {
            endLoading(dispatch, 'add-batch-override-entries');
            throw e;
        }
    },

    async updateBatchEntries({ dispatch }, { pricebookId, filters, params }) {
        const url = Laroute.route(
            'franchise.inventory.override_pricebook.batch_update',
            {
                id: pricebookId,
            },
        );

        startLoading(dispatch, 'update-batch-override-entries');
        try {
            const { data } = await axios.post(url, {
                filters: filters,
                params: params,
            });

            endLoading(dispatch, 'update-batch-override-entries');

            return data;
        } catch (e) {
            endLoading(dispatch, 'update-batch-override-entries');
            throw e;
        }
    },

    setOverridePricebookAttr({ commit }, { key, value }) {
        commit('setOverridePricebookAttr', { key, value });
    },

    async saveExistingOverride({ state, dispatch }, params) {
        const id = state.pricebook.id;
        const url = Laroute.route(
            'franchise.inventory.override_pricebook.existing.save',
            {
                id,
            },
        );
        startLoading(dispatch, 'save-override-pricebook');

        try {
            const { data } = await axios.post(url, params);
            endLoading(dispatch, 'save-override-pricebook');
            console.log(params);
            return { data };
        } catch (e) {
            endLoading(dispatch, 'save-override-pricebook');
            throw e;
        }
    },

    async getAverageCost({ commit, dispatch }, params) {
        const url = Laroute.route(
            'franchise.inventory.override_pricebook.average.cost',
        );

        startLoading(dispatch, 'get-average-cost');

        try {
            const { data } = await axios.get(url, { params });
            commit('setAverageCost', data);
            endLoading(dispatch, 'get-average-cost');

            return data;
        } catch (e) {
            endLoading(dispatch, 'get-average-cost');
            throw e;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/override_pricebook.js